// packages/internal-sdk/src/public/investment/useInvestInAgent.ts
import { useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
// ✅ CORRECTED: Import the new, correct service
import { investService, isApiError } from '@dopomogai/api-client';
import { AgentKitError } from '../../types';
import type { InvestInAgentPayload, InvestCreateResponse } from '@dopomogai/types';
import { useAnalytics } from '../../analytics/useAnalytics';

type InvestHookPayload = {
  agent_id: string;
  shares_to_buy: number;
  terms_version: string;
  idempotency_key?: string;
};

/**
 * A hook for triggering a secure, idempotent investment in an agent.
 * Handles loading, error states, and ensures network retries don't double-charge.
 */
export const useInvestInAgent = () => {
  const analytics = useAnalytics();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AgentKitError | null>(null);
  const [data, setData] = useState<InvestCreateResponse | null>(null);

  const attemptKeyRef = useRef<string | null>(null);

  /**
   * Resets the current investment attempt, allowing a new, distinct transaction to be made.
   * Call this if the user closes the modal and reopens it to try again.
   */
  const resetAttempt = useCallback(() => {
    attemptKeyRef.current = null;
  }, []);

  const invest = useCallback(
    async (payload: InvestHookPayload): Promise<InvestCreateResponse | null> => {
      if (!payload.agent_id || !payload.shares_to_buy || payload.shares_to_buy <= 0) {
        setError(new AgentKitError('Invalid agent ID or number of shares.'));
        return null;
      }
      if (!payload.terms_version) {
        setError(new AgentKitError('Investment terms must be accepted.'));
        return null;
      }

      setIsLoading(true);
      setError(null);
      setData(null);

      if (payload.idempotency_key) {
        attemptKeyRef.current = payload.idempotency_key;
      } else if (!attemptKeyRef.current) {
        attemptKeyRef.current = uuidv4();
      }

      const { idempotency_key: _ignore, ...rest } = payload as any;
      const apiPayload: InvestInAgentPayload = {
        ...rest,
        idempotency_key: attemptKeyRef.current!,
      };

      // Track the user's intent
      analytics.track('invest_attempt', {
        agent_id: apiPayload.agent_id,
        shares: apiPayload.shares_to_buy,
      });

      try {
        // ✅ CORRECTED: Calls the correct service method.
        const response = await investService.makeInvestment(apiPayload);
        
        if (isApiError(response)) {
          const err = new AgentKitError('Failed to complete investment', response);
          setError(err);
          // Track the failure
          analytics.track('invest_failed', {
            agent_id: apiPayload.agent_id,
            shares: apiPayload.shares_to_buy,
            error_code: response.status,
            http_status: response.status,
          });
          // Don't reset the attempt key here, so the user can retry the same failed transaction.
          return null;
        }

        // Track the success
        analytics.track('invest_succeeded', {
          agent_id: apiPayload.agent_id,
          shares: response.shares_purchased,
          new_balance_credits: response.new_balance_credits,
        });

        setData(response);
        resetAttempt(); // Success! Reset for the next distinct transaction.
        return response;
      } catch (e: any) {
        const err = new AgentKitError('A network error occurred while investing.', e);
        setError(err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [analytics, resetAttempt]
  );

  return {
    invest,
    data,
    isLoading,
    error,
    resetAttempt,
  };
};